import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n';
import { QrCode } from '../components/QrCode';
import { TestBar } from '../components/TestBar';
import './Landing.css';

function initialAppUrl(): string {
  if (typeof window === 'undefined') return 'https://singlings.com/app';
  return `${window.location.origin}/app`;
}

function isLocalHostname(h: string): boolean {
  return h === 'localhost' || h === '127.0.0.1' || h === '::1' || h === '0.0.0.0';
}

export function Landing() {
  const { t } = useT();
  const [url, setUrl] = useState(initialAppUrl);

  // In dev, the default origin is http://localhost:5174 — useless from a phone.
  // Ask the backend for the host's LAN IPv4 and rebuild the URL so the QR scans.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const { hostname, port, protocol } = window.location;
    if (!isLocalHostname(hostname)) return;

    const controller = new AbortController();
    fetch('/v1/hostinfo', { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { lanIps?: string[] } | null) => {
        const ip = data?.lanIps?.[0];
        if (!ip) return;
        const portPart = port ? `:${port}` : '';
        setUrl(`${protocol}//${ip}${portPart}/app`);
      })
      .catch(() => {
        /* offline / endpoint disabled — keep the localhost URL */
      });
    return () => controller.abort();
  }, []);
  return (
    <main className="landing">
      <div className="landing__hero">
        <div className="landing__demo">
          <TestBar />
          <div className="landing__mascots" aria-hidden="true">
            <Blob scale={0.5} color="var(--singling-s)" tilt={-3} />
            <Blob scale={0.65} color="var(--singling-a)" tilt={2} />
            <Blob scale={0.8} color="var(--singling-t)" tilt={-1} />
            <Blob scale={1} color="var(--singling-b)" tilt={2} />
          </div>
        </div>
        <h1 className="landing__tagline">{t('landing.tagline')}</h1>
        <p className="landing__lede">{t('landing.explainer')}</p>
        <div className="landing__qr">
          <div className="landing__qr-card">
            <QrCode value={url} size={176} />
          </div>
          <div className="landing__qr-copy">
            <p className="landing__qr-label">{t('landing.scanQr')}</p>
            <p className="landing__qr-url">{url}</p>
            <Link to="/app" className="landing__open-link">
              {t('landing.openApp')} →
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}

function Blob({ scale, color, tilt }: { scale: number; color: string; tilt: number }) {
  const w = 78 * scale;
  const h = 130 * scale;
  return (
    <div
      className="landing__blob"
      style={{
        width: w,
        height: h,
        background: color,
        transform: `rotate(${tilt}deg)`,
      }}
    >
      <span className="landing__blob-eye" style={{ left: `28%`, top: `38%` }} />
      <span className="landing__blob-eye" style={{ right: `28%`, top: `38%` }} />
      <span className="landing__blob-mouth" />
    </div>
  );
}
